import type {
  InventoryReadinessBundleResponse,
  InventoryReadinessReport,
  InventoryReportStatus,
} from '@/types/inventoryPlatform';

const STATUS_STYLES: Record<InventoryReportStatus, string> = {
  PASS: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  WARN: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  FAIL: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  INFO: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300',
  NOT_APPLICABLE: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
};

function StatusBadge({ status }: { status: InventoryReportStatus }) {
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-semibold ${STATUS_STYLES[status]}`}>{status}</span>
  );
}

function ReportBlock({ report }: { report: InventoryReadinessReport }) {
  return (
    <section className="card p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-semibold text-slate-900 dark:text-slate-100">{report.title}</h3>
          <p className="text-xs text-slate-500">{report.summary}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold text-primary-600">{report.readinessScore}/100</span>
          <StatusBadge status={report.overallStatus} />
        </div>
      </div>
      <ul className="space-y-3">
        {report.checks.map((check) => (
          <li key={check.code} className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <p className="font-medium text-sm">{check.title}</p>
              <StatusBadge status={check.status} />
            </div>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{check.message}</p>
            {check.details && check.details.length > 0 && (
              <ul className="mt-2 list-inside list-disc text-xs text-slate-500">
                {check.details.slice(0, 8).map((d) => (
                  <li key={d}>{d}</li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function InventoryReadinessPanel({ bundle }: { bundle: InventoryReadinessBundleResponse }) {
  return (
    <div className="space-y-4">
      <div className="card border-primary-200 bg-primary-50/50 p-4 dark:border-primary-900 dark:bg-primary-950/30">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">Enterprise readiness</h2>
            <p className="text-sm text-slate-600 dark:text-slate-400">{bundle.summary}</p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-primary-600">{bundle.overallReadinessScore}/100</p>
            <StatusBadge status={bundle.overallStatus} />
          </div>
        </div>
      </div>
      <ReportBlock report={bundle.integrity} />
      <ReportBlock report={bundle.valuation} />
      <ReportBlock report={bundle.warehouseOperations} />
      <ReportBlock report={bundle.financeReconciliation} />
      <ReportBlock report={bundle.supplyChain} />
    </div>
  );
}
